import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, CheckIcon } from 'lucide-react';
import { CompanySettings, InvoicePayment, PaymentMethod, ProInvoice } from '../../../types/pro';
import { formatDate, krPrecise, newLineId, toISODate } from '../../../utils/pro';
import { invoiceAmounts } from '../../../utils/invoice';
import { Modal } from '../Modal';
import { SendDocumentDialog } from '../SendDocumentDialog';
const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
const label = 'mb-1.5 block text-sm font-medium text-stone-700';
const ghost = 'rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100';
const primary = 'rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400';

// ---------------------------------------------------------------------------

interface ApproveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: ProInvoice;
  onApprove: () => void;
}

/**
 * Approving locks the invoice, so the numbers that matter are repeated here
 * rather than asking "are you sure?" about something off-screen.
 */
export function ApproveDialog({
  isOpen,
  onClose,
  invoice,
  onApprove
}: ApproveDialogProps) {
  const sums = invoiceAmounts(invoice);
  const rows = [['Kunde', invoice.billing.name || invoice.customerName || '—'], ['Fakturanummer', invoice.invoiceNo], ['Total inkl. moms', krPrecise(sums.total)], ['Forfaldsdato', formatDate(invoice.dueDate)]];
  return <Modal isOpen={isOpen} onClose={onClose} title="Er du sikker på, at fakturaen er klar?" subtitle="Efter godkendelse kan linjer og beløb ikke længere rettes." size="sm" footer={<>
          <button onClick={onClose} className={ghost} data-id="element-247" data-mp="1ax159o3ohz22">
            Tilbage
          </button>
          <button onClick={() => {
      onApprove();
      onClose();
    }} className={primary} data-id="element-248" data-mp="18kgrl40ualv3">
            Godkend faktura
          </button>
        </>} data-id="element-246" data-mp="3cp73ocflgzyb">
      <dl className="divide-y divide-stone-100 overflow-hidden rounded-lg border border-stone-200" data-id="element-249" data-mp="zj95dkbllbhd">
        {rows.map(([term, value]) => <div key={term} className="flex justify-between gap-4 px-4 py-2.5" data-id="element-250" data-mp="1s86bkp2jbhtr">
            <dt className="text-sm text-stone-500" data-id="element-251" data-mp="1n8lc8djgd7gi">{term}</dt>
            <dd className="text-right text-sm font-semibold text-stone-900" data-id="element-252" data-mp="1n93im2ztx225">
              {value}
            </dd>
          </div>)}
      </dl>

      {invoice.lines.length === 0 && <p className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-id="element-253" data-mp="1dhz39268rf6s">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-254" data-mp="2azqqwpdnptp8" />
          Fakturaen har ingen linjer — den vil lyde på 0 kr.
        </p>}
    </Modal>;
}

// ---------------------------------------------------------------------------

interface SendDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: ProInvoice;
  company: CompanySettings;
  onSend: () => void;
  onOpenPreview: () => void;
}
export function SendDialog({
  isOpen,
  onClose,
  invoice,
  company,
  onSend,
  onOpenPreview
}: SendDialogProps) {
  return <SendDocumentDialog isOpen={isOpen} onClose={onClose} kind="Faktura" documentNo={invoice.invoiceNo} recipientName={invoice.billing.name || invoice.customerName} recipientEmail={invoice.billing.email} attention={invoice.billing.attention} company={company} onSent={onSend} onOpenPreview={onOpenPreview} data-id="element-255" data-mp="2whpxdy8zfs2n" />;
}

// ---------------------------------------------------------------------------

const METHODS: PaymentMethod[] = ['Bankoverførsel', 'Kontant', 'MobilePay', 'Kort', 'Andet'];
interface PaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: ProInvoice;
  onRegister: (payment: InvoicePayment) => void;
}
export function PaymentDialog({
  isOpen,
  onClose,
  invoice,
  onRegister
}: PaymentDialogProps) {
  const sums = invoiceAmounts(invoice);
  const [date, setDate] = useState(toISODate(new Date()));
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('Bankoverførsel');

  // Default to the outstanding amount — paying in full is the normal case.
  useEffect(() => {
    if (!isOpen) return;
    setDate(toISODate(new Date()));
    setAmount(sums.outstanding.toFixed(2).replace('.', ','));
    setMethod('Bankoverførsel');
  }, [isOpen, sums.outstanding]);
  const value = parseFloat(amount.replace(',', '.')) || 0;
  const remaining = Math.max(0, sums.outstanding - value);
  return <Modal isOpen={isOpen} onClose={onClose} title="Registrer betaling" subtitle={`${invoice.invoiceNo} · ${krPrecise(sums.outstanding)} udestående`} size="sm" footer={<>
          <button onClick={onClose} className={ghost} data-id="element-257" data-mp="1qo4ppoc8q646">
            Annuller
          </button>
          <button onClick={() => {
      if (value <= 0) return;
      onRegister({
        id: newLineId(),
        date,
        amount: value,
        method
      });
      onClose();
    }} disabled={value <= 0} className={`inline-flex items-center gap-2 ${primary}`} data-id="element-258" data-mp="1usku1l67h5ha">
            <CheckIcon className="h-4 w-4" data-id="element-259" data-mp="2eeh6zptdlf7f" />
            Registrer betaling
          </button>
        </>} data-id="element-256" data-mp="1n6gxhsnax7v4">
      <div className="space-y-4" data-id="element-260" data-mp="1zpuioylrtd42">
        <label className="block" data-id="element-261" data-mp="24oxmygtxffaz">
          <span className={label} data-id="element-262" data-mp="24o5tbbjw3rnc">Betalingsdato</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={field} data-id="element-263" data-mp="2ses0l0m2l7wa" />
        </label>
        <label className="block" data-id="element-264" data-mp="2x7l2zhu2g457">
          <span className={label} data-id="element-265" data-mp="3718m178gq9pw">Beløb (kr)</span>
          <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" className={`${field} tabular-nums`} data-id="element-266" data-mp="370pcadsyurmy" />
        </label>
        <label className="block" data-id="element-267" data-mp="2x1hqd2861oo8">
          <span className={label} data-id="element-268" data-mp="2t1zdmtqi0jwh">Betalingsmetode</span>
          <select value={method} onChange={e => setMethod(e.target.value as PaymentMethod)} className={field} data-id="element-269" data-mp="2t1fvpd0ulvig">
            {METHODS.map(option => <option key={option} value={option} data-id="element-270" data-mp="2jalambzc91yl">
                {option}
              </option>)}
          </select>
        </label>

        <p className="rounded-lg bg-stone-50 p-3 text-sm text-stone-600" data-id="element-271" data-mp="29e8g5bvyyk2s">
          {remaining <= 0.01 ? <>
              Fakturaen bliver markeret som{' '}
              <span className="font-semibold text-emerald-700" data-id="element-272" data-mp="25dy35f9retef">Betalt</span>.
            </> : <>
              Der vil mangle{' '}
              <span className="font-semibold tabular-nums text-stone-900" data-id="element-273" data-mp="fu7yjti6cutt">
                {krPrecise(remaining)}
              </span>{' '}
              efter denne betaling.
            </>}
        </p>

        {invoice.payments.length > 0 && <ul className="divide-y divide-stone-100 overflow-hidden rounded-lg border border-stone-200 text-sm" data-id="element-274" data-mp="tuimoos3nvba">
            {invoice.payments.map(payment => <li key={payment.id} className="flex items-center justify-between gap-3 px-3 py-2" data-id="element-275" data-mp="3t7t3gsdxzaab">
                <span className="text-stone-500" data-id="element-276" data-mp="64oewlmn0wxw">
                  {formatDate(payment.date)} · {payment.method}
                </span>
                <span className="font-semibold tabular-nums text-stone-900" data-id="element-277" data-mp="axhfqyezjl4p">
                  {krPrecise(payment.amount)}
                </span>
              </li>)}
          </ul>}
      </div>
    </Modal>;
}